import React, { useMemo, useState } from "react";

import {
  ArrayUtils,
  formatDayDate,
  formatNumber,
  getAdminVerificationYearlyDataYear,
  getCropName,
  getEditingRequestButtonState,
  useFertilizerHelpers,
  useGetFieldPractices,
  useGetPracticeOrganicFertilizerApplications,
  useInView,
  useUpdateReviewedDataPoints,
  useUpdateVerificationStatus,
} from "utils";

import {
  CropFCPO,
  EDITING_REQUEST_TYPE,
  EditingRequestType,
  FieldVerification,
  PRACTICE_SLUG,
  PracticeFCPO,
  ReviewedDataPoint,
  VerificationEditingRequest,
  VerificationStatus,
  ZERO_UUID,
} from "models";

import { Button, Col, Icon, Message, Row, SkeletonWrapper, TagNormal, Tooltip, Typography } from "components";

import { CropDrawer } from "pages/admin/verifications/components/misc/CropDrawer";
import { EditingRequestModal, EditingRequestTarget, TargetableRequestType } from "pages/admin/verifications/components/misc/EditingRequestModal";
import {
  ORGANIC_FERTILIZER_REVIEWED_DATA_POINT_OPTIONS,
  ReviewedDataPointsDropdown,
} from "pages/admin/verifications/components/misc/ReviewedDataPointsDropdown";
import { VerificationStatusDropdown } from "pages/admin/verifications/components/misc/VerificationStatusDropdown";

// Maps a fertiliser practice's slug to the (singular) editing-request type it targets
const FERTILIZER_PRACTICE_REQUEST_TYPE: Record<string, TargetableRequestType> = {
  [PRACTICE_SLUG.MINERAL_FERTILIZERS]: EDITING_REQUEST_TYPE.MINERAL_FERTILIZER,
  [PRACTICE_SLUG.ORGANIC_FERTILIZERS]: EDITING_REQUEST_TYPE.ORGANIC_FERTILIZER,
};

interface FertilizerTableProps {
  field: FieldVerification;
  farmId: string;
  editingRequests: VerificationEditingRequest[];
}

export const FertilizerTable = ({ field, farmId, editingRequests }: FertilizerTableProps) => {
  const [cropToEdit, setCropToEdit] = useState<CropFCPO | null>(null);
  const [editingRequestTarget, setEditingRequestTarget] = useState<EditingRequestTarget>(null);
  const { getFertilizerName, getFertilizerNValue } = useFertilizerHelpers();
  const { data: applicationTypes } = useGetPracticeOrganicFertilizerApplications({ enabled: true });

  // Helper function to get editing requests for a specific fertilizer row.
  // "Missing fertiliser" requests are created directly against the "no fertilizer applied" (ZERO_UUID) row,
  // so this also covers that row's requests — no separate year-level "missing practice" flow needed.
  const getFertilizerEditingRequests = (fertilizerId: string, requestType: EditingRequestType): VerificationEditingRequest[] =>
    editingRequests.filter(
      (request) =>
        request.requestType === requestType && (request.fieldMineralFertilizerId === fertilizerId || request.fieldOrganicFertilizerId === fertilizerId)
    );

  // Helper function to get application type name
  const getApplicationTypeName = (applicationTypeId?: string) => {
    if (!applicationTypeId) return null;
    return applicationTypes?.find((a) => a.id === applicationTypeId)?.name ?? null;
  };

  const fieldId = field.id;
  const { ref, inView } = useInView({ threshold: 0.1, triggerOnce: true });

  const { data: fieldPractices, isLoading } = useGetFieldPractices({ fieldId, inView, isAdmin: true });

  // Handler for clicking on a fertilizer row to open the crop drawer
  const handleFertilizerClick = (fertilizer: PracticeFCPO) => {
    const associatedCrop = fieldPractices?.crops?.find((c) => c.id === fertilizer.cropId);
    if (associatedCrop) {
      setCropToEdit(associatedCrop);
    }
  };

  const { mutate: updateStatus } = useUpdateVerificationStatus({ fieldId, farmId });
  const { mutate: updateReviewedDataPoints } = useUpdateReviewedDataPoints({ fieldId });

  // Handler for updating fertilizer verification status
  const handleStatusChange = (fertilizerId: string, fertilizerType: string, newStatus: VerificationStatus) => {
    // Allow null to reset verification status
    updateStatus(
      {
        practiceType: fertilizerType,
        practiceId: fertilizerId,
        verificationStatus: newStatus,
      },
      {
        onSuccess: () => {
          Message({ icon: "tick", content: "Fertilizer verification status updated successfully" });
        },
        onError: () => {
          Message({ icon: "error", content: "Failed to update fertilizer verification status" });
        },
      }
    );
  };

  // Handler for updating which data points were reviewed on an organic fertilizer
  const handleReviewedDataPointsChange = (fertilizerId: string, fertilizerType: string, reviewedDataPoints: ReviewedDataPoint[] | null) => {
    updateReviewedDataPoints(
      { practiceType: fertilizerType, practiceId: fertilizerId, reviewedDataPoints },
      {
        onSuccess: () => {
          Message({ icon: "tick", content: "Fertilizer reviewed data points updated successfully" });
        },
      }
    );
  };

  // Filter practices to get only fertilizers
  const fertilizers = fieldPractices?.practices?.filter(
    (practice) => practice.type === PRACTICE_SLUG.MINERAL_FERTILIZERS || practice.type === PRACTICE_SLUG.ORGANIC_FERTILIZERS
  );

  // Requests whose target row no longer exists (e.g. it was deleted and replaced rather than edited in place)
  const currentFertilizerIds = new Set((fertilizers ?? []).map((fertilizer) => fertilizer.id).filter((id): id is string => !!id));
  const orphanedFertilizerEditingRequests = editingRequests.filter(
    (request) =>
      (request.requestType === EDITING_REQUEST_TYPE.MINERAL_FERTILIZER || request.requestType === EDITING_REQUEST_TYPE.ORGANIC_FERTILIZER) &&
      request.reviewedAt === null &&
      !currentFertilizerIds.has((request.fieldMineralFertilizerId ?? request.fieldOrganicFertilizerId) || "")
  );
  const [showOrphanedEditingRequests, setShowOrphanedEditingRequests] = useState(false);

  // Easter egg: Clemens' Cozy Cottage - 1/20 chance for self-produced organic fertilizer to show a golden home
  const cozyHomeMap = useMemo(() => {
    const map = new Map<string, boolean>();
    fertilizers?.forEach((fertilizer) => {
      if (fertilizer.type === PRACTICE_SLUG.ORGANIC_FERTILIZERS && fertilizer.selfProduced === true && fertilizer.id) {
        const isCozyHome = Math.random() < 1 / 20;
        if (isCozyHome) {
          map.set(fertilizer.id, true);
        }
      }
    });
    return map;
  }, [fertilizers]);

  // Easter egg: Nils' Nitrogen Ninja - 1/10 chance for mineral fertilizer with N-value > 25%
  const nilsNinjaMap = useMemo(() => {
    const map = new Map<string, boolean>();
    fertilizers?.forEach((fertilizer) => {
      if (fertilizer.type === PRACTICE_SLUG.MINERAL_FERTILIZERS && fertilizer.id) {
        const nValue = getFertilizerNValue(fertilizer);
        if (nValue !== null && nValue !== undefined && nValue > 0.25) {
          const isNilsNinja = Math.random() < 1 / 10;
          if (isNilsNinja) {
            map.set(fertilizer.id, true);
          }
        }
      }
    });
    return map;
  }, [fertilizers, getFertilizerNValue]);

  // Group fertilizers by year
  const fertilizersByYear = ArrayUtils.groupBy(fertilizers ?? [], (fertilizer) => fertilizer?.year ?? "unknown");

  // Sort years in descending order
  const sortedYears = Object.keys(fertilizersByYear || {})
    .filter((year) => year !== "unknown")
    .map(Number)
    .sort((a, b) => b - a);

  return (
    <div ref={ref} className="crop-table">
      <SkeletonWrapper isLoading={isLoading} type="block" height="20rem">
        {orphanedFertilizerEditingRequests.length > 0 &&
          (() => {
            const buttonState = getEditingRequestButtonState(orphanedFertilizerEditingRequests);
            return (
              <Row className="year-header" align="middle" justify="end">
                <Col>
                  <Button type={buttonState.type} className={`${buttonState.className} margin-right-250`} onClick={() => setShowOrphanedEditingRequests(true)}>
                    {`${buttonState.text} (${orphanedFertilizerEditingRequests.length})`}
                  </Button>
                </Col>
              </Row>
            );
          })()}
        {/* Table Header */}
        <Row className="crop-table-header">
          <Col md={4} className="header-cell">
            fertilizer name
          </Col>
          <Col md={2} className="header-cell">
            amount
          </Col>
          <Col md={2} className="header-cell">
            N-Value
          </Col>
          <Col md={2} className="header-cell">
            date
          </Col>
          <Col md={2} className="header-cell">
            application type
          </Col>
          <Col md={1} className="header-cell">
            self produced
          </Col>
          <Col md={2} className="header-cell">
            selection type
          </Col>
          <Col md={3} className="header-cell">
            status
          </Col>
          <Col md={3} className="header-cell">
            reviewed data points
          </Col>
          <Col md={3} className="header-cell"></Col>
        </Row>

        {/* Table Body grouped by year */}
        {sortedYears.map((year) => {
          // Sort fertilizers by application date (endDate) in descending order (most recent first)
          const yearFertilizers = [...fertilizersByYear[year]].sort((a, b) => {
            const aDate = a.endDate ?? a.fallbackDates.endDate;
            const bDate = b.endDate ?? b.fallbackDates.endDate;
            if (!aDate && !bDate) return 0;
            if (!aDate) return 1;
            if (!bDate) return -1;
            return new Date(bDate).getTime() - new Date(aDate).getTime();
          });
          return (
            <div key={year} className="year-group">
              {/* Year header */}
              <Row className="year-header" align="middle">
                <Col md={4}>
                  <Typography variant="h4" className="year-title">
                    {year}
                  </Typography>
                </Col>
                <Col md={2}></Col>
                <Col md={2}></Col>
                <Col md={2}></Col>
                <Col md={2}></Col>
                <Col md={1}></Col>
                <Col md={2}></Col>
                <Col md={3}></Col>
                <Col md={3}></Col>
                <Col md={3}></Col>
              </Row>
              {/* Fertilizers for this year */}
              {yearFertilizers.map((fertilizer) => {
                const cropName = getCropName(fertilizer.cropId, fieldPractices);
                const isPurchasedOrganicFertilizer =
                  fertilizer.type === PRACTICE_SLUG.ORGANIC_FERTILIZERS && fertilizer.selfProduced === false && fertilizer.assignedPracticeId !== ZERO_UUID;
                return (
                  <Row
                    key={fertilizer.id}
                    className="crop-row pointer"
                    onClick={() => handleFertilizerClick(fertilizer)}
                    style={isPurchasedOrganicFertilizer ? { borderLeft: "3px solid #e65100" } : undefined}
                  >
                    <Col md={4} className={`crop-cell ${fertilizer.soilPlus3DataCollection ? "highlight-soil-plus3-collection" : ""}`}>
                      <div className="crop-name-cell">
                        <div className={`crop-indicator ${fertilizer.verificationStatus}`} />
                        <Icon
                          iconName={
                            fertilizer.type === PRACTICE_SLUG.MINERAL_FERTILIZERS
                              ? fertilizer.assignedPracticeId === ZERO_UUID
                                ? "tabler/droplet-off"
                                : "tabler/droplet"
                              : "tabler/poo"
                          }
                          fill={fertilizer.assignedPracticeId !== ZERO_UUID ? "currentColor" : "none"}
                          className="icon inline margin-right-50"
                          size="s"
                        />
                        <div style={{ display: "flex", flexDirection: "column", gap: "0.125rem" }}>
                          <Typography variant="paragraph-s" bold>
                            {getFertilizerName(fertilizer.type, fertilizer.assignedPracticeId)}
                          </Typography>
                          {cropName && (
                            <Typography variant="paragraph-xs" color="grey-600">
                              {cropName}
                            </Typography>
                          )}
                        </div>
                      </div>
                    </Col>
                    <Col md={2} className="crop-cell">
                      <Typography variant="paragraph-xs">
                        {fertilizer.assignedPracticeId === ZERO_UUID || !fertilizer.amount
                          ? "-"
                          : `${formatNumber({ number: fertilizer.amount, decimalPlaces: 2 })} ${
                              fertilizer.type === PRACTICE_SLUG.MINERAL_FERTILIZERS ? "kg/ha" : "m³/ha"
                            }`}
                      </Typography>
                    </Col>
                    <Col md={2} className="crop-cell">
                      {(() => {
                        if (fertilizer.assignedPracticeId === ZERO_UUID) {
                          return <Typography variant="paragraph-xs">-</Typography>;
                        }
                        const nValue = getFertilizerNValue(fertilizer);
                        const isMineral = fertilizer.type === PRACTICE_SLUG.MINERAL_FERTILIZERS;
                        const isNilsNinja = fertilizer.id && nilsNinjaMap.has(fertilizer.id);

                        if (nValue === null || nValue === undefined) {
                          return <Typography variant="paragraph-xs">-</Typography>;
                        }

                        const displayValue = `${formatNumber({ number: isMineral ? nValue * 100 : nValue, decimalPlaces: 2 })} ${
                          isMineral ? "% N" : "kg N/m³"
                        }`;

                        return (
                          <Tooltip
                            title={
                              isNilsNinja
                                ? "🥷 Nils' Nitrogen Ninja™ strikes from the shadows! Stealthy nutrients detected. +12 N-Value Precision, +8 Mineral Mastery"
                                : ""
                            }
                          >
                            <Typography variant="paragraph-xs" className={isNilsNinja ? "orange-text" : ""}>
                              {isNilsNinja && "🥷 "}
                              {displayValue}
                            </Typography>
                          </Tooltip>
                        );
                      })()}
                    </Col>
                    <Col md={2} className={`crop-cell ${fertilizer.applicationDatesSoilPlus3DataCollection ? "highlight-soil-plus3-collection" : ""}`}>
                      <Typography variant="paragraph-xs">
                        {fertilizer.assignedPracticeId === ZERO_UUID
                          ? "-"
                          : fertilizer.endDate ?? fertilizer.fallbackDates.endDate
                          ? formatDayDate((fertilizer.endDate ?? fertilizer.fallbackDates.endDate)!)
                          : "-"}
                      </Typography>
                    </Col>
                    <Col md={2} className="crop-cell">
                      <Typography variant="paragraph-xs">
                        {fertilizer.type === PRACTICE_SLUG.ORGANIC_FERTILIZERS && fertilizer.assignedPracticeId !== ZERO_UUID
                          ? getApplicationTypeName(fertilizer.applicationType) ?? "-"
                          : "-"}
                      </Typography>
                    </Col>
                    <Col md={1} className="crop-cell">
                      {fertilizer.assignedPracticeId === ZERO_UUID || fertilizer.selfProduced == null ? (
                        <Typography variant="paragraph-xs">-</Typography>
                      ) : fertilizer.type === PRACTICE_SLUG.ORGANIC_FERTILIZERS ? (
                        (() => {
                          const isCozyHome = fertilizer.id && cozyHomeMap.has(fertilizer.id);
                          const cozyTooltip = isCozyHome
                            ? "🏡 You found Clemens' Place! Wait... which Clemens? Both? The mystery deepens. +15 Confusion Resistance, +10 Self-Produced Pride"
                            : "";
                          const colorClass = fertilizer.selfProduced ? (isCozyHome ? "orange-text" : "green-text") : "orange-text";
                          return (
                            <Tooltip title={cozyTooltip}>
                              <Typography
                                variant="paragraph-xs"
                                className={`${colorClass}`}
                                style={{ display: "inline-flex", alignItems: "center", gap: "0.25rem" }}
                                title={!isCozyHome ? (fertilizer.selfProduced ? "Produced on the farm" : "Purchased / brought from outside") : undefined}
                              >
                                <Icon iconName={fertilizer.selfProduced ? "tabler/home" : "tabler/truck"} size="s" className="icon inline" />
                                {fertilizer.selfProduced ? "Yes" : "No"}
                              </Typography>
                            </Tooltip>
                          );
                        })()
                      ) : (
                        <Typography variant="paragraph-xs">{fertilizer.selfProduced ? "Yes" : "No"}</Typography>
                      )}
                    </Col>
                    <Col md={2} className="crop-cell">
                      {fertilizer.selectionType ? <TagNormal text={fertilizer.selectionType} color="blue" /> : "-"}
                    </Col>
                    <Col md={3} className="crop-cell">
                      <VerificationStatusDropdown
                        status={fertilizer.verificationStatus ?? null}
                        cropId={fertilizer.id!}
                        onStatusChange={(cropId, status) => handleStatusChange(cropId, fertilizer.type, status)}
                      />
                    </Col>
                    <Col md={3} className="crop-cell">
                      {fertilizer.type === PRACTICE_SLUG.ORGANIC_FERTILIZERS ? (
                        <ReviewedDataPointsDropdown
                          reviewedDataPoints={fertilizer.reviewedDataPoints ?? null}
                          practiceId={fertilizer.id!}
                          options={ORGANIC_FERTILIZER_REVIEWED_DATA_POINT_OPTIONS}
                          onChange={(id, points) => handleReviewedDataPointsChange(id, fertilizer.type, points)}
                        />
                      ) : (
                        <Typography variant="paragraph-xs">-</Typography>
                      )}
                    </Col>
                    <Col md={3} className="crop-cell">
                      {(() => {
                        const requestType = FERTILIZER_PRACTICE_REQUEST_TYPE[fertilizer.type];
                        const fertilizerEditingRequests = getFertilizerEditingRequests(fertilizer.id!, requestType);
                        const buttonState = getEditingRequestButtonState(fertilizerEditingRequests);

                        return (
                          <Button
                            type={buttonState.type}
                            className={buttonState.className}
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingRequestTarget({
                                requestType,
                                id: fertilizer.id!,
                                label: getFertilizerName(fertilizer.type, fertilizer.assignedPracticeId) ?? "this fertiliser",
                              });
                            }}
                          >
                            {buttonState.text}
                          </Button>
                        );
                      })()}
                    </Col>
                  </Row>
                );
              })}
            </div>
          );
        })}

        {/* Empty state */}
        {sortedYears.length === 0 && !isLoading && (
          <Row className="empty-state">
            <Col md={24}>
              <Typography variant="paragraph-s" className="text-disabled">
                No fertilizers found for this field
              </Typography>
            </Col>
          </Row>
        )}
      </SkeletonWrapper>
      <CropDrawer
        crop={cropToEdit}
        showDrawer={!!cropToEdit}
        onClose={() => setCropToEdit(null)}
        fieldPractices={fieldPractices}
        field={field}
        farmId={farmId}
      />
      <EditingRequestModal
        target={editingRequestTarget}
        fieldId={fieldId}
        showModal={!!editingRequestTarget}
        onClose={() => setEditingRequestTarget(null)}
        farmId={farmId}
        editingRequests={editingRequests}
        verificationYear={getAdminVerificationYearlyDataYear()}
      />
      <EditingRequestModal
        reviewOnlyEditingRequests={orphanedFertilizerEditingRequests}
        fieldId={fieldId}
        showModal={showOrphanedEditingRequests}
        onClose={() => setShowOrphanedEditingRequests(false)}
        farmId={farmId}
        editingRequests={editingRequests}
        verificationYear={getAdminVerificationYearlyDataYear()}
      />
    </div>
  );
};
